// PlayerGameLog — 2026-07-27 (per Gilles)
// ─────────────────────────────────────────────────────────────────────────────
// The "receipts" behind a leaderboard row: every mini-game the player
// appeared in — date, mini #, side, score, W/L/T, GF/GA — fetched from
// GET /teams/:teamId/stats/player-games/:userId. Rendered inline when a
// leaderboard row is expanded (coach StatsPage AND the player Team
// Leaderboard on MyStatsPage share this one component so the two views
// can never drift).
//
// The endpoint mirrors the aggregate rules exactly (played units only,
// unscored units listed with no result, goalie side-swap applied when
// the team setting is on) — so the visible rows always sum to the
// numbers on the row the user just clicked.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';
import api from '../services/api.js';

const RESULT_STYLE = {
  W:    { label: 'W', color: 'var(--goal)',    bg: 'var(--goal-a12, rgba(0,232,122,0.12))' },
  L:    { label: 'L', color: 'var(--penalty)', bg: 'rgba(255,68,68,0.12)' },
  T:    { label: 'T', color: 'var(--puck)',    bg: 'rgba(255,149,0,0.12)' },
  none: { label: '·', color: 'var(--text-3)',  bg: 'var(--surface-2)' },
};

export default function PlayerGameLog({ teamId, userId, seasonParam }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [privateStats, setPrivateStats] = useState(false); // 2026-09-17 stat privacy (403 STATS_PRIVATE)

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(false);
    // Season scope must MATCH the leaderboard the user clicked: pass
    // seasonParam through verbatim when the parent page has one
    // (StatsPage), omit it entirely otherwise (MyStatsPage) so the
    // backend resolves the same default (team's active season).
    api.get(`/teams/${teamId}/stats/player-games/${userId}`, {
      params: seasonParam ? { seasonId: seasonParam } : {},
    })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.data?.code === 'STATS_PRIVATE') setPrivateStats(true);
        else setError(true);
      });
    return () => { cancelled = true; };
  }, [teamId, userId, seasonParam]);

  if (privateStats) {
    return (
      <div className="text-xs text-secondary px-2 py-2">
        🔒 This player keeps their game-by-game stats private.
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-xs text-secondary px-2 py-2">
        Couldn't load the game-by-game detail.
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex items-center gap-2 px-2 py-2 text-xs text-secondary">
        <div className="spinner" style={{ width: 12, height: 12 }} /> Loading games…
      </div>
    );
  }
  if (!data.games || data.games.length === 0) {
    return (
      <div className="text-xs text-secondary px-2 py-2">
        No mini-games on record yet for this player.
      </div>
    );
  }

  // Friendly date without TZ surprises: 'YYYY-MM-DD' → local noon.
  const fmtDate = (d) => {
    try {
      return new Date(`${d}T12:00:00`).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric',
      });
    } catch { return d; }
  };

  return (
    <div className="mt-1" style={{ borderTop: '1px dashed var(--border)', paddingTop: 8 }}>
      {data.swapApplied && (
        <div className="text-xs mb-2" style={{ color: 'var(--ice)' }}>
          🔁 Goalie sides swap each mini-game — results below follow the switch.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {data.games.map(gm => {
          const rs = RESULT_STYLE[gm.result] || RESULT_STYLE.none;
          return (
            <div key={gm.gameId}
              className="flex items-center gap-2 flex-wrap rounded-lg px-2 py-1.5"
              style={{ background: 'var(--surface-2)' }}>
              {/* W/L/T chip */}
              <span className="flex-shrink-0 rounded-md text-center font-bold"
                style={{
                  width: 22, fontSize: 11, padding: '2px 0',
                  color: rs.color, background: rs.bg,
                  fontFamily: 'var(--font-display)',
                }}>
                {rs.label}
              </span>
              {/* Date + mini # */}
              <span className="text-xs font-semibold" style={{ minWidth: 52 }}>
                {fmtDate(gm.date)}
              </span>
              <span className="text-xs text-secondary">
                {gm.opponent
                  ? <>vs <span className="font-semibold" style={{ color: 'var(--text)' }}>{gm.opponent}</span></>
                  : <>{gm.isMini ? `Mini ${gm.miniIndex + 1}` : 'Game'}{' · '}{gm.side === 'home' ? 'Home' : 'Away'} side</>}
              </span>
              {/* Score + GF/GA, right-aligned */}
              <span className="text-xs ml-auto" style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-2)' }}>
                {gm.result === 'none'
                  ? 'no score recorded'
                  : <>
                      {gm.homeScore}–{gm.awayScore}
                      <span style={{ marginLeft: 8, color: 'var(--text-3)' }}>
                        GF {gm.gf} · GA {gm.ga}
                      </span>
                    </>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
